import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { NativeSelect, SegmentedControl } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import { campaignPath, stageWord } from "../lib/campaign";
import { Dialog } from "../ui/Dialog";

/* Putting a campaign in a sequence, or moving it in one, from its page, and
 * choosing its place when it is made (the builder's Sequence question).
 *
 * A rep's campaigns line up in sequences, each starting after the one ahead
 * of it (James, 2026-11-02). Start after puts this campaign behind the one
 * chosen, Start before puts it ahead, which is the only way to the front.
 * The choices are every campaign still running or waiting, grouped by owner
 * in the order of their sequences, so moving a campaign to another rep is
 * choosing one of theirs, and the dialog says the owner changes. The engine
 * closes the gap the campaign leaves, changes the owner, and refuses a move
 * that breaks a rule with its own sentence (api.sequence_campaign, engine
 * migration 311, and api.create_campaign, engine migration 314). Take out of
 * sequence is an action on the page. The builder and the dialog use the same
 * picker, so the two list the same campaigns the same way.
 * Engine docs/plans/campaign-sequencing.md. */

export type SequenceTarget = Pick<Campaign, "name" | "label" | "stage" | "owner_name" | "owner_id" | "sequence_place"
  | "sequence_length" | "sequence_head_name" | "follows_name" | "auto_enrich" | "ask_enricher" | "ask_apollo">;
export type SequenceWhere = "after" | "before";

/** The engine's sentence for a campaign's place, "2nd in its sequence,
 *  after Texas. Then Illinois.", with each campaign it names a link to that
 *  campaign's page, as every name is (docs/design-standards.md). The
 *  sentence names the campaign ahead after ", after " and the one behind
 *  after " Then " (lake.campaign_sequence, engine migration 311). A name not
 *  found there stays text. */
export function SequenceLine({ c }: {
  c: Pick<Campaign, "sequence_words" | "follows_name" | "follows_label" | "followed_by_name" | "followed_by_label">;
}) {
  const marks = [
    { before: ", after ", label: c.follows_label, name: c.follows_name },
    { before: " Then ", label: c.followed_by_label, name: c.followed_by_name },
  ];
  const out: ReactNode[] = [];
  let rest = c.sequence_words ?? "";
  for (const m of marks) {
    if (!m.label || !m.name) continue;
    const at = rest.indexOf(`${m.before}${m.label}.`);
    if (at < 0) continue;
    const start = at + m.before.length;
    out.push(rest.slice(0, start), <Link key={m.name} to={campaignPath(m.name)}>{m.label}</Link>);
    rest = rest.slice(start + m.label.length);
  }
  out.push(rest);
  return <>{out}</>;
}

/** Every campaign still running or waiting, by owner in sequence order. */
export function useSequenceTargets(token: string, enabled = true) {
  return useQuery({
    queryKey: ["sequence-targets"],
    enabled,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign")
        .select("name,label,stage,owner_name,owner_id,sequence_place,sequence_length,sequence_head_name,follows_name,auto_enrich,ask_enricher,ask_apollo")
        .is("archived_at", null).in("stage", ["draft", "enriching", "ready_to_call", "calling"])
        // The engine's order, owner, then sequence, then place, which the
        // Campaigns list's Sequence column sorts by too (migration 314).
        .order("sequence_order", { ascending: true, nullsFirst: false }).limit(500);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as SequenceTarget[];
    },
  });
}

/** The campaign that will be ahead of one put after or before `target`: the
 *  one chosen, or the one now ahead of it. A campaign being moved leaves its
 *  place first, so when it is the one ahead of `target`, the campaign ahead
 *  of it takes that place. Used only to say what a press starts. The engine
 *  decides. */
export function aheadOf(targets: SequenceTarget[], where: SequenceWhere, target: string,
                        self?: { name: string; follows_name?: string | null | undefined }): SequenceTarget | null {
  const find = (n?: string | null) => (n ? targets.find((t) => t.name === n) ?? null : null);
  const chosen = find(target);
  if (!chosen || where === "after") return chosen;
  const ahead = find(chosen.follows_name);
  return ahead && self && ahead.name === self.name ? find(self.follows_name) : ahead;
}

/** Start after or Start before, and the campaign. */
export function SequencePicker({ targets, exclude, where, target, onWhere, onTarget, emptyLabel, description, maw, autoFocus }: {
  targets: SequenceTarget[];
  exclude?: string | null | undefined;
  where: SequenceWhere;
  target: string;
  onWhere: (where: SequenceWhere) => void;
  onTarget: (target: string) => void;
  emptyLabel: string;
  description?: string;
  maw?: string;
  autoFocus?: boolean;
}) {
  // Grouped by owner, in sequence order. A campaign with no owner is listed,
  // and the engine says why it cannot be chosen.
  const groups = new Map<string, { value: string; label: string }[]>();
  for (const t of targets) {
    if (!t.name || t.name === exclude) continue;
    const owner = t.owner_name ?? "No owner";
    const place = (t.sequence_length ?? 1) > 1 ? `, ${t.sequence_place} of ${t.sequence_length} in its sequence` : "";
    const items = groups.get(owner) ?? [];
    items.push({ value: t.name, label: `${t.label ?? t.name} (${stageWord(t.stage)}${place})` });
    groups.set(owner, items);
  }
  const data = [{ value: "", label: emptyLabel },
    ...[...groups.entries()].map(([group, items]) => ({ group, items }))];
  return (
    <>
      <SegmentedControl fullWidth value={where} aria-label="Where it goes" maw={maw}
                        onChange={(v) => onWhere(v as SequenceWhere)}
                        data={[{ value: "after", label: "Start after" }, { value: "before", label: "Start before" }]} />
      <NativeSelect mt="md" label="Campaign" value={target} data={data} maw={maw} description={description}
                    data-autofocus={autoFocus ? true : undefined}
                    onChange={(e) => onTarget(e.currentTarget.value)} />
    </>
  );
}

export function SequenceDialog({ token, c, opened, onClose, onDone }: {
  token: string;
  c: Campaign;
  opened: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [where, setWhere] = useState<SequenceWhere>("after");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const targets = useSequenceTargets(token);

  const all = targets.data ?? [];
  const chosen = all.find((t) => t.name === target);
  const newOwner = chosen && chosen.owner_id && chosen.owner_id !== c.owner_id ? chosen.owner_name : null;
  // Said before the press because it starts work: a campaign in Planning
  // with enrichment chosen starts it when it lands behind one being called
  // (lake.start_sequence_follower, engine migration 311).
  const ahead = aheadOf(all, where, target, { name: c.name ?? "", follows_name: c.follows_name });
  const startsNow = ahead?.stage === "calling" && c.stage === "draft"
    && Boolean(c.auto_enrich || c.ask_enricher || c.ask_apollo);
  // A campaign being called put ahead of the first of a sequence starts that
  // one's chosen enrichment too.
  const targetStarts = where === "before" && c.stage === "calling" && chosen?.stage === "draft"
    && !chosen.follows_name && Boolean(chosen.auto_enrich || chosen.ask_enricher || chosen.ask_apollo);
  const moving = (c.sequence_length ?? 1) > 1;

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await callFunction(token, "sequence_campaign", where === "after"
        ? { p_name: c.name ?? "", p_after: target }
        : { p_name: c.name ?? "", p_before: target });
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog opened={opened} title={moving ? `Move ${c.label ?? c.name} in its sequence` : `Put ${c.label ?? c.name} in a sequence`} onClose={onClose}
            action={{ label: where === "after" ? "Start after it" : "Start before it", onPress: save }}
            busy={busy} disabled={!target || targets.isLoading}
            error={error ?? (targets.isError ? (targets.error as Error).message : null)}>
      <SequencePicker targets={all} exclude={c.name} where={where} target={target} emptyLabel="Choose a campaign" autoFocus
                      onWhere={(v) => { setError(null); setWhere(v); }}
                      onTarget={(v) => { setError(null); setTarget(v); }} />
      {newOwner ? <p className="muted">This makes {newOwner} the owner.</p> : null}
      {startsNow ? <p className="muted">Its enrichment starts now, because {ahead?.label ?? ahead?.name} is being called.</p> : null}
      {targetStarts ? <p className="muted">{chosen?.label ?? chosen?.name} starts its enrichment now, because {c.label ?? c.name} is being called.</p> : null}
      {/* Moving it closes the gap it leaves, in the engine's words
          (api.campaign.when_out_words, engine migration 314). */}
      {moving && target && c.when_out_words ? <p className="muted">{c.when_out_words}</p> : null}
    </Dialog>
  );
}
